import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ispPackageAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';

// Standalone page, reached via a direct SMS/WhatsApp link with the reference
// code pre-filled (?code=DWI-XXXXX) — deliberately not nested under
// ProtectedRoute/AppLayout so it renders (and keeps the code in the URL)
// whether or not the visitor is logged in yet.
export default function LinkPackage() {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  const codeFromUrl = params.get('code') || '';

  const [form, setForm] = useState({ referenceCode: codeFromUrl, fullName: '', area: '' });
  const [linking, setLinking] = useState(false);
  const [err, setErr] = useState('');
  const [linked, setLinked] = useState(null); // subscription returned on success

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.referenceCode.trim() || !form.fullName.trim() || !form.area.trim()) {
      setErr('Reference code, full name, and area are all required.');
      return;
    }
    setLinking(true);
    setErr('');
    try {
      const r = await ispPackageAPI.linkExisting(form);
      setLinked(r.subscription);
    } catch (e2) {
      setErr(e2.response?.data?.message || e2.message || 'Could not link that reference code.');
    } finally {
      setLinking(false);
    }
  };

  const styles = {
    page:  { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: 'var(--bg-page, #0a1628)' },
    hero:  { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', padding: '32px 20px', color: '#fff', textAlign: 'center' },
    title: { fontSize: 22, fontWeight: 700, margin: '0 0 8px' },
    sub:   { fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, margin: 0 },
    body:  { padding: '24px 20px' },
    codeBox: { background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 10,
               padding: '14px 16px', textAlign: 'center', marginBottom: 20 },
    codeLbl: { fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' },
    codeVal: { fontFamily: 'monospace', fontSize: 22, fontWeight: 800, color: '#22d3ee', letterSpacing: '0.08em', marginTop: 4 },
    label: { fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4, marginTop: 14 },
    input: { width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
             border: '1px solid var(--border)', background: 'var(--bg-page)',
             color: 'var(--text-primary)', fontSize: 14 },
    btn:   { width: '100%', marginTop: 20, padding: '13px 0', borderRadius: 10,
             background: '#22d3ee', color: '#0f172a', fontWeight: 700, fontSize: 15,
             border: 'none', cursor: 'pointer' },
    btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
    secondaryBtn: { display: 'block', textAlign: 'center', width: '100%', marginTop: 10, padding: '12px 0',
                    borderRadius: 10, border: '1px solid var(--border)', color: 'var(--text-primary)',
                    fontWeight: 600, fontSize: 14, textDecoration: 'none', boxSizing: 'border-box' },
    errBox: { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 14 },
    successBox: { background: 'var(--bg-card)', border: '2px solid #22d3ee', borderRadius: 14, padding: 20, textAlign: 'center' },
    successTitle: { fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px' },
    successDetail: { fontSize: 14, color: 'var(--text-muted)', margin: '4px 0' },
  };

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <h1 style={styles.title}>Link Your Existing Internet Package</h1>
        <p style={styles.sub}>
          Already a DWIFI customer from before this app existed? Enter the code we gave you
          to bring your package in here — you'll be able to see its expiry and renew in one tap
          from now on, no more calling in.
        </p>
      </div>

      <div style={styles.body}>
        {linked ? (
          <div style={styles.successBox}>
            <p style={styles.successTitle}>Linked!</p>
            <p style={styles.successDetail}>{linked.packageNameSnapshot || 'Your package'}</p>
            {linked.expiresAt && (
              <p style={styles.successDetail}>
                Active until {new Date(linked.expiresAt).toLocaleDateString()}
              </p>
            )}
            <p style={{ ...styles.successDetail, marginTop: 14 }}>
              It's now in your account — check My Purchases anytime.
            </p>
            <Link to="/internet" style={{ ...styles.btn, display: 'block', textDecoration: 'none', boxSizing: 'border-box' }}>
              Go to My Purchases
            </Link>
          </div>
        ) : authLoading ? (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</p>
        ) : !isAuthenticated ? (
          <>
            <div style={styles.codeBox}>
              <div style={styles.codeLbl}>Your reference code</div>
              <div style={styles.codeVal}>{codeFromUrl || '—'}</div>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6, textAlign: 'center' }}>
              You'll need a DWI Fintech account first. Log in or create one, then tap this
              same link again to finish linking your package.
            </p>
            <Link to={`/login`} style={styles.secondaryBtn}>Log In</Link>
            <Link to={`/register`} style={{ ...styles.secondaryBtn, background: '#22d3ee', color: '#0f172a', border: 'none' }}>
              Create Account
            </Link>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <label style={styles.label}>Reference code</label>
            <input
              style={styles.input}
              type="text"
              placeholder="DWI-XXXXX"
              value={form.referenceCode}
              onChange={e => setForm(f => ({ ...f, referenceCode: e.target.value }))}
            />

            <label style={styles.label}>Full name</label>
            <input
              style={styles.input}
              type="text"
              value={form.fullName}
              onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
            />

            <label style={styles.label}>Area / location</label>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g. Kirinya near police"
              value={form.area}
              onChange={e => setForm(f => ({ ...f, area: e.target.value }))}
            />

            {err && <div style={styles.errBox}>{err}</div>}

            <button
              type="submit"
              style={{ ...styles.btn, ...(linking ? styles.btnDisabled : {}) }}
              disabled={linking}
            >
              {linking ? 'Linking…' : 'Link Package'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
